import { useParams, usePathname } from 'next/navigation'
import { StringifiableRecord, stringifyUrl } from 'query-string'

import { createPdpBaseUrl } from '../../app/_components/navigation/createPdpUrl'
import { createReviewsPageUrl } from '../../app/_components/navigation/createReviewsPageUrl'
import { optionalLocaleURLRegex } from '../server-middleware/expressMiddlewareUtils'
import { singleQueryParamValue } from '../utils/next'

/**
 * Strips locale prefix from pathname if present, otherwise returns the original pathname.
 * Handles URLs like /es-MX/collections/... by returning /collections/...
 */
export function stripLocaleFromPathname(pathname: string): string {
  const matches = pathname.match(optionalLocaleURLRegex)
  return matches?.[1] ? matches[2] ?? '/' : pathname
}

/**
 * @deprecated Use urlPath from ProductCatalog graphql response data.
 */
export function useKitHref(handle: string, query: StringifiableRecord = {}) {
  return stringifyUrl({
    url: `/kits/${handle}`,
    query,
  })
}

/**
 * Generates a product href that preserves collection context when on collection product pages.
 * Specifically designed for use cases like style links where you want to stay within the same collection.
 */
/**
 * @deprecated Use urlPath from ProductCatalog graphql response data.
 */
export function useCollectionPersistentProductHref(
  productGroupHandle: string,
  query: Record<string, string | undefined>
): string {
  const params = useParams()
  const pathname = usePathname()
  const isOnCollectionScopedPage = pathname?.startsWith('/collections/') && params?.collectionHandle

  const collectionHandle = isOnCollectionScopedPage
    ? singleQueryParamValue(params.collectionHandle) || undefined
    : undefined

  return stringifyUrl({
    url: createPdpBaseUrl({
      productGroupHandle,
      collectionHandle,
      isKit: false,
      isExclusive: false,
      genderKey: null,
    }),
    query,
  })
}

export function useProductReviewsHref({
  handle,
  colorKey,
  query = {},
}: {
  handle: string
  query?: StringifiableRecord
  colorKey: string | undefined | null
}) {
  return stringifyUrl({
    url: createReviewsPageUrl({
      colorKey,
      handle,
      type: 'read-review',
    }),
    query,
  })
}

export function useProductWriteReviewHref({
  handle,
  colorKey,
  query = {},
}: {
  handle: string
  query?: StringifiableRecord
  colorKey: string | undefined | null
}) {
  return stringifyUrl({
    url: createReviewsPageUrl({
      colorKey,
      handle,
      type: 'write-review',
    }),
    query,
  })
}

export function portalCollectionHref(handle: string, query: StringifiableRecord = {}) {
  return stringifyUrl({
    url: `/portals/${handle}`,
    query,
  })
}

export function giftProductHref(
  handle: string,
  slug: string,
  giftItemCollectionId: string,
  collectionHandle: string,
  query: StringifiableRecord = {}
) {
  return stringifyUrl({
    url: `/claim-gift/${slug}/collection/${giftItemCollectionId}/${collectionHandle}/product/${handle}`,
    query,
  })
}

export function giftCollectionHref(
  giftItemCollectionId: string,
  collectionHandle: string,
  slug: string,
  query: StringifiableRecord = {}
) {
  return stringifyUrl({
    url: `/claim-gift/${slug}/collection/${giftItemCollectionId}/${collectionHandle}`,
    query,
  })
}

export function giftCartHref(slug: string) {
  return `/claim-gift/${slug}/cart`
}
